import React, { useState } from 'react'
import { CAlert, CButton, CFormLabel, CFormSelect, CFormTextarea, CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { edApplyScript } from './EditorView'
import { state as bostonState, Storage, uuidV4 } from '../lib/store'
import { useUI } from '../../../shared/ui/UIProvider'
import { aiCallAgent, aiUsageRecord } from '../lib/ai'
import { biaCancelAIJobWait } from '../lib/biaApi'
import { csLoadRuntimeTemplates, csBuildSingleFile, csCmdLabel, csFieldNoWrite } from '../lib/builder'
import { applyTransactionIntent, normalizeDataJson } from '../lib/dataJsonNormalize'
import { lintDataJson, lintErrors, lintFixPrompt } from '../lib/dataJsonLint'
import { validateRepairPreservation } from '../lib/dataJsonRepairGuard'
import {
  reservationGenerationContract,
  reservationInterpretationGuide,
  validateReservationRequirements,
} from '../lib/reservationRequirements'

/* Port fiel de viewAiBuild/bindAiBuild/aiGenerate + renderAiPreview/bindAiPreview (prefijo 'aib')
   de boston_ia: constructor de Custom Scripts desde lenguaje natural via Agente 3.
   Nota: aiGenerateScript/aiChatEditScript pertenecen al flujo Importador ('aii'), no a este. */

/* Port de init() original: restaurar la API key guardada (el store no la hidrata). */
/* ===== showPreviewModal — port VERBATIM (modal DOM autocontenido, igual que el original) ===== */
function showPreviewModal(dataJson, configJson, opts = {}) {
  // Si ya hay un modal abierto, lo limpiamos
  const prev = document.getElementById('boston-preview-modal')
  if (prev) prev.remove()

  const overlay = document.createElement('div')
  overlay.id = 'boston-preview-modal'
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:99998;background:rgba(15,23,42,.7);display:flex;align-items:center;justify-content:center;padding:24px;backdrop-filter:blur(2px)'

  const title = (dataJson && dataJson.title) || bostonState.cs.title || 'Custom Script'
  const realPreview = opts.realPreview === true
  if (realPreview && !bostonState.sessionToken) {
    alert('Falta token Sabre para ejecutar comandos reales.')
    return
  }
  const btnBase = 'padding:7px 12px;border-radius:6px;cursor:pointer;font:600 12px/1.2 system-ui'
  const previewNotice = realPreview
    ? '<b>Vista previa real:</b> usa el token Sabre y puede ejecutar comandos reales.'
    : '<b>Vista previa segura:</b> las llamadas a Sabre estan simuladas. El form, validacion, postCommands y placeholders se ejecutan reales.'

  overlay.innerHTML = `
    <div style="background:#fff;border-radius:12px;width:min(1100px,100%);height:min(90vh,860px);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 30px 80px rgba(0,0,0,.4)">
      <div style="padding:14px 20px;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;gap:12px;background:linear-gradient(135deg,#0f766e,#0e7490);color:#fff">
        <div style="flex:1">
          <div style="font-size:11px;opacity:.85;letter-spacing:.5px;text-transform:uppercase">Vista previa funcional - Single-file</div>
        <span>${previewNotice}</span>
        </div>
        <button id="boston-prev-download" style="${btnBase};background:rgba(255,255,255,.15);color:#fff;border:1px solid rgba(255,255,255,.3)" title="Descargar .html autocontenido">Descargar HTML único</button>
        <button id="boston-prev-newtab" style="${btnBase};background:rgba(255,255,255,.15);color:#fff;border:1px solid rgba(255,255,255,.3)" title="Abrir en pestaña nueva">Pestaña nueva</button>
        <button id="boston-prev-close" style="${btnBase};background:#ef4444;color:#fff;border:0" title="Cerrar">× Cerrar</button>
      </div>
      <div style="background:#fef3c7;color:#92400e;padding:8px 20px;font-size:12px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #fcd34d">
        <span>⚠</span>
        <span><b>Preview mode:</b> las llamadas a Sabre están simuladas. El form, validación, postCommands y placeholders sí se ejecutan reales.</span>
      </div>
      <div style="flex:1;position:relative;background:#f8fafc">
        <div id="boston-prev-loading" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#64748b;font:500 14px/1.5 system-ui">
          <div style="text-align:center"><div>Empaquetando script en un único archivo…</div></div>
        </div>
        <iframe id="boston-prev-iframe" title="Vista previa funcional" sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin" style="width:100%;height:100%;border:0;background:#fff;display:none"></iframe>
      </div>
      <div style="padding:8px 20px;background:#f1f5f9;border-top:1px solid #e2e8f0;font:12px/1.4 system-ui;color:#475569;display:flex;justify-content:space-between;align-items:center">
        <span><b>${(dataJson && dataJson.forms && dataJson.forms.length) || 0}</b> form(s) - <b>${(dataJson && dataJson.postCommands && dataJson.postCommands.length) || 0}</b> postCommand(s)</span>
        <span style="opacity:.7">UUID: <code>${String((configJson && configJson.config && configJson.config.uuid) || '—').replace(/</g, '&lt;')}</code></span>
      </div>
    </div>
  `
  document.body.appendChild(overlay)

  const close = () => {
    window.removeEventListener('message', onScriptCloseMsg)
    overlay.remove()
  }
  // La X interna del script (runtime) avisa por postMessage cuando corre embebido.
  function onScriptCloseMsg(e) {
    if (e && e.data && e.data.type === 'boston-script-close') close()
  }
  window.addEventListener('message', onScriptCloseMsg)
  overlay.querySelector('#boston-prev-close').addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') {
      close()
      document.removeEventListener('keydown', esc)
    }
  })

  // Construir el single-file y meterlo en el iframe
  ;(async () => {
    try {
      // Forzar refresh del cache del runtime para que el preview SIEMPRE use la última app.js de disco
      await csLoadRuntimeTemplates(true)
      const singleHtml = await csBuildSingleFile(dataJson, configJson, {
        previewMode: !realPreview,
        simulateApi: !realPreview,
        realPreview,
        token: bostonState.sessionToken,
        env: bostonState.cs.targetEnv || 'stg',
        title,
      })
      const blob = new Blob([singleHtml], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const iframe = overlay.querySelector('#boston-prev-iframe')
      const loading = overlay.querySelector('#boston-prev-loading')
      iframe.src = url
      iframe.addEventListener('load', () => {
        loading.style.display = 'none'
        iframe.style.display = 'block'
      })

      // Botón descargar: genera el .html SIN previewMode (es el de producción)
      overlay.querySelector('#boston-prev-download').addEventListener('click', () => {
        csBuildSingleFile(dataJson, configJson, { previewMode: false, title }).then((prodHtml) => {
          const pblob = new Blob([prodHtml], { type: 'text/html' })
          const purl = URL.createObjectURL(pblob)
          const a = document.createElement('a')
          a.href = purl
          const slug = String(title).toLowerCase().replace(/[^a-z0-9_-]+/g, '_').slice(0, 40) || 'custom_script'
          a.download = slug + '.html'
          document.body.appendChild(a)
          a.click()
          a.remove()
          setTimeout(() => URL.revokeObjectURL(purl), 1000)
        })
      })

      // Botón pestaña nueva
      overlay.querySelector('#boston-prev-newtab').addEventListener('click', () => {
        window.open(url, '_blank')
      })

      // Limpiar URL del blob cuando se cierre
      overlay.querySelector('#boston-prev-close').addEventListener('click', () => {
        setTimeout(() => URL.revokeObjectURL(url), 100)
      })
    } catch (e) {
      console.error('[boston] error en preview:', e)
      const loading = overlay.querySelector('#boston-prev-loading')
      if (loading) {
        loading.innerHTML =
          '<div style="text-align:center;color:#dc2626"><div><b>Error armando el preview</b></div><div style="font-size:12px;margin-top:6px;opacity:.85">' +
          String(e.message || e).replace(/</g, '&lt;') +
          '</div><div style="font-size:11px;margin-top:8px;opacity:.6">Revisá la consola para detalles.</div></div>'
      }
    }
  })()
}

function normalizeAssistantBuildPayload(payload) {
  const dataJson = payload?.dataJson || payload?.['data.json'] || payload
  const logicJson = payload?.logicJson || payload?.['logic.json'] || null
  const handlerJs = payload?.handlerJs || payload?.['app.js'] || ''
  const fichaMd = payload?.fichaMd || payload?.['FICHA.md'] || ''
  return { dataJson, logicJson, handlerJs, fichaMd }
}

function buildDataJsonEjecutable(data) {
  if (!data || typeof data !== 'object') return false
  const hasForms = Array.isArray(data.forms) && data.forms.some((form) => Array.isArray(form?.remarks) && form.remarks.length)
  const hasPostCommands = Array.isArray(data.postCommands) && data.postCommands.length > 0
  const hasOnLoad = Array.isArray(data.onLoad) && data.onLoad.length > 0
  return hasForms || hasPostCommands || hasOnLoad || Boolean(data.autoRun)
}

function diagnosticText(diagnostic) {
  if (typeof diagnostic === 'string') return diagnostic
  if (!diagnostic || typeof diagnostic !== 'object') return String(diagnostic || '')
  const code = diagnostic.code ? '[' + diagnostic.code + '] ' : ''
  return code + String(diagnostic.message || diagnostic.title || '')
}

/* ===== renderFormPreview / renderScriptFieldsPreview — port a JSX ===== */
function fieldWrite(f) {
  const code = f.block != null && f.block !== '' && !isNaN(f.block) && Number(f.block) < 90 ? String(f.block) : ''
  const val = f.dato != null && f.dato !== '' ? f.dato : '«' + (f.label || f.tag || 'valor') + '»'
  return code + (f.remark_mask || '').replace(/\[[^\]]*\]/g, val)
}

function FieldsPreview({ cs }) {
  const forms = cs.forms || []
  const post = cs.postCommands || []
  const hasFields = forms.some((f) => f.remarks && f.remarks.length > 0)
  const hasCmds = post.length > 0
  if (!hasFields && !hasCmds) {
    return <p className="bia-empty">Agrega campos al form (o postCommands) para ver el preview.</p>
  }
  return (
    <>
      <h4 style={{ margin: '14px 0 8px', color: '#1b9e3e', fontSize: 14 }}>Campos del formulario (vista del agente)</h4>
      <div style={{ fontSize: 11, color: '#768192', margin: '-4px 0 8px' }}>
        Las marcadas{' '}
        <span className="bia-pill mini" style={{ background: 'rgba(148,163,184,.25)' }}>
          auto - oculta
        </span>{' '}
        las arma el script solo (grupo oculto): el vendedor no las ve, pero igual se escriben.
      </div>
      {forms.length === 0 ? (
        <p className="bia-empty">Sin forms — solo postCommands.</p>
      ) : (
        forms.map((form, fi) => (
          <div key={fi} style={{ border: '1px solid #d8dbe0', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {forms.length > 1 ? (
                <span className="bia-pill mini">
                  Paso {fi + 1} de {forms.length}
                </span>
              ) : null}
              <strong style={{ fontSize: 14 }}>{form.title || form.form || 'Form ' + (fi + 1)}</strong>
            </div>
            {(form.remarks || []).length === 0 ? (
              <p className="bia-empty" style={{ padding: 8, fontSize: 12 }}>
                Sin campos en este paso.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.remarks.map((field, idx) => {
                  const hidden = /oculto/i.test(field.group || '')
                  const writes = fieldWrite(field)
                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: hidden ? 'rgba(148,163,184,.12)' : '#f8f9fa',
                        borderRadius: 4,
                        fontSize: 12.5,
                        opacity: hidden ? 0.6 : 1,
                      }}
                    >
                      <code style={{ background: '#0f172a', color: '#22d989', padding: '2px 6px', borderRadius: 3, fontSize: 11, flexShrink: 0 }}>
                        {field.campoForm || 'field_' + idx}
                      </code>
                      <div style={{ flex: 1 }}>
                        <strong>{field.label || '(sin label)'}</strong>
                        {field.mandatory ? <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span> : null}
                        {hidden ? (
                          <span
                            className="bia-pill mini"
                            style={{ marginLeft: 6, background: 'rgba(148,163,184,.25)' }}
                            title="Línea automática: el script la arma sola y va en un grupo oculto; el vendedor no la ve"
                          >
                            auto - oculta
                          </span>
                        ) : null}
                        <div style={{ fontSize: 11, color: '#768192', marginTop: 2 }}>
                          <span className="bia-pill mini">{field.typedata || 'text'}</span>{' '}
                          {field.typeremark && field.typeremark !== 'General' ? (
                            <span className="bia-pill mini">{field.typeremark}</span>
                          ) : null}{' '}
                          {csFieldNoWrite(field) ? (
                            <span
                              className="bia-pill mini"
                              title="Dato interno o elemento visual: el runtime NO emite ningún comando por este campo"
                              style={{ opacity: 0.75 }}
                            >
                              no escribe
                            </span>
                          ) : writes ? (
                            <>
                              - escribe: <code>{writes}</code>
                            </>
                          ) : null}{' '}
                          {field.data_test ? (
                            <>
                              - Ej: <code>{String(field.data_test)}</code>
                            </>
                          ) : null}{' '}
                          {(field.datasource || []).length ? <>- {field.datasource.length} opciones</> : null}
                        </div>
                        {(field.datasource || []).length ? (
                          <div style={{ fontSize: 11, color: '#768192', marginTop: 3 }}>
                            {field.datasource.slice(0, 5).map((o, oi) => (
                              <code key={oi} style={{ background: 'rgba(0,0,0,.05)', padding: '1px 4px', borderRadius: 2, marginRight: 3 }}>
                                {o.text || o.value || ''}
                              </code>
                            ))}
                            {field.datasource.length > 5 ? <small> ...y {field.datasource.length - 5} más</small> : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))
      )}
      {post.length ? (
        <>
          <h4 style={{ margin: '14px 0 8px', color: '#1b9e3e', fontSize: 14 }}>postCommands ({post.length})</h4>
          <ol
            style={{
              margin: 0,
              padding: '10px 14px 10px 32px',
              background: '#0f172a',
              borderRadius: 6,
              color: '#e2e8f0',
              fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 12,
            }}
          >
            {post.map((c, i) => (
              <li key={i}>
                <code style={{ color: '#22d989' }}>{csCmdLabel(c)}</code>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </>
  )
}

function AiBuildView() {
  const state = useBostonState()
  const { toast } = useUI()
  const mockModeEnabled = new URLSearchParams(window.location.search).get('mock') === '1'
  const a = state.ai
  const hasKey = true
  const [prompt, setPrompt] = useState(a.userPrompt || '')
  const [originalRequest, setOriginalRequest] = useState(a.userPrompt || '')
  // Chat de interpretación: el agente SIEMPRE explica cómo entendió el pedido
  // antes de generar; el usuario corrige conversando y recién ahí se genera.
  const [chat, setChat] = useState(null) // { messages: [{role:'agent'|'user', text}] }
  const [chatInput, setChatInput] = useState('')
  const [interpBusy, setInterpBusy] = useState(false)

  function chatLastAgentText(current) {
    const messages = (current?.messages || []).filter((m) => m.role === 'agent')
    return messages.length ? messages[messages.length - 1].text : ''
  }

  function esProcesoDetenido(err) {
    return err && /detenido|aborted|abort/i.test(String(err.message || ''))
  }

  /* Botón "Detener": corta la espera del job de IA (como el stop de cualquier IA). */
  function detenerProceso() {
    biaCancelAIJobWait()
  }

  /* PASO 1: interpretar el pedido en lenguaje natural (sin generar JSON). */
  async function aiInterpretar() {
    if (state.ai.busy || interpBusy) return
    const texto = (state.ai.userPrompt || '').trim()
    if (!texto) {
      toast('Escribí qué script necesitás.', 'warning')
      return
    }
    setInterpBusy(true)
    setOriginalRequest(texto)
    setChat(null)
    setChatInput('')
    state.ai.lastRawError = null
    emit()
    try {
      const promptInterp =
        'MODO INTERPRETACIÓN — en esta respuesta NO generes JSON ni código.\n' +
        'El usuario pidió el siguiente Custom Script en lenguaje natural. Antes de construirlo, explicá en español y en texto plano cómo lo interpretás: objetivo del script, formularios y campos (tipo de dato, obligatoriedad), remarks/comandos Sabre que se enviarían, y los supuestos que estás tomando. Si hay ambigüedades, listalas al final como preguntas.\n' +
        reservationInterpretationGuide() + '\n\n' +
        'PEDIDO DEL USUARIO:\n' + texto
      const r = await aiCallAgent('agent3_gen', promptInterp, false, { generationMode: 'natural' })
      if (r.usage) aiUsageRecord(r.model, r.usage, 'build_interpret')
      const textoInterp = String((r && r.data && r.data.data) || (r && r.raw) || '').trim()
      if (!r.ok || !textoInterp) throw new Error('El agente no devolvió una interpretación válida.')
      setChat({ messages: [{ role: 'agent', text: textoInterp }] })
    } catch (err) {
      if (esProcesoDetenido(err)) {
        toast('Proceso detenido.', 'warning')
      } else {
        state.ai.lastRawError = { message: err.message, rawExcerpt: '' }
        emit()
      }
    } finally {
      setInterpBusy(false)
    }
  }

  /* Chat: aplicar una corrección del usuario y re-interpretar. */
  async function aiEnviarCorreccion() {
    const correccion = chatInput.trim()
    if (!correccion || !chat || state.ai.busy || interpBusy) return
    const nextMessages = [...chat.messages, { role: 'user', text: correccion }]
    setChat({ ...chat, messages: nextMessages })
    setChatInput('')
    setInterpBusy(true)
    try {
      const historial = nextMessages
        .map((m) => (m.role === 'agent' ? 'INTERPRETACIÓN DEL AGENTE:\n' : 'CORRECCIÓN DEL USUARIO:\n') + m.text)
        .join('\n\n---\n\n')
      const promptCorr =
        'MODO INTERPRETACIÓN — en esta respuesta NO generes JSON ni código.\n' +
        'Estás refinando con el usuario la interpretación de un Custom Script Sabre. Aplicá la ÚLTIMA corrección del usuario y devolvé la interpretación COMPLETA actualizada (no solo el cambio), en texto plano y con el mismo nivel de detalle. Si la corrección introduce una ambigüedad nueva, aclarala al final.\n' +
        reservationInterpretationGuide() + '\n\n' +
        'PEDIDO ORIGINAL:\n' + String(originalRequest || prompt || state.ai.userPrompt || '').trim() + '\n\n---\n\n' +
        historial
      const r = await aiCallAgent('agent3_gen', promptCorr, false, { generationMode: 'edit' })
      if (r.usage) aiUsageRecord(r.model, r.usage, 'build_interpret')
      const textoNuevo = String((r && r.data && r.data.data) || (r && r.raw) || '').trim()
      if (!r.ok || !textoNuevo) throw new Error('El agente no pudo aplicar la corrección.')
      setChat({ ...chat, messages: [...nextMessages, { role: 'agent', text: textoNuevo }] })
    } catch (err) {
      if (esProcesoDetenido(err)) {
        toast('Proceso detenido.', 'warning')
      } else {
        state.ai.lastRawError = { message: err.message, rawExcerpt: '' }
        emit()
      }
    } finally {
      setInterpBusy(false)
    }
  }

  /* PASO 2: el usuario confirmó — el texto interpretado pasa al constructor y se genera. */
  function aiConfirmarYGenerar() {
    if (!chat || state.ai.busy || interpBusy) return
    const textoConfirmado = chatLastAgentText(chat)
    if (!textoConfirmado) return
    const textoOriginal = String(originalRequest || prompt || state.ai.userPrompt || '').trim()
    // La interpretación guía la construcción, pero el pedido original se conserva
    // para que ninguna obligación desaparezca durante el resumen del agente.
    state.ai.userPrompt = textoOriginal
    setPrompt(textoOriginal)
    setChat(null)
    setChatInput('')
    aiGenerate(textoConfirmado, textoOriginal)
  }

  /* ===== aiGenerate — port fiel (alert → toast, render → emit) ===== */
  async function aiGenerate(textoConfirmado = '', textoOriginal = '') {
    if (state.ai.busy) return
    const entrada = String(textoConfirmado || state.ai.userPrompt || '').trim()
    const pedidoOriginal = String(textoOriginal || originalRequest || state.ai.userPrompt || entrada).trim()
    if (!entrada) {
      toast('Escribí qué script necesitás.', 'warning')
      return
    }
    state.ai.busy = true
    state.ai.lastRawError = null
    emit()
    try {
      const { ok, data, model, usage, raw } = await aiCallAgent(
        'agent3_gen',
        'Generá un Custom Script data.json. Debes cumplir tanto el PEDIDO ORIGINAL como la INTERPRETACIÓN CONFIRMADA; si el resumen omitió un requisito explícito del pedido original, conserva el requisito original.\n\n' +
          reservationGenerationContract() +
          '\n\nPEDIDO ORIGINAL:\n' + pedidoOriginal +
          '\n\nINTERPRETACIÓN CONFIRMADA:\n' + entrada,
        true,
        { generationMode: 'natural' },
      )
      if (!ok || !data.data) {
        state.ai.busy = false
        state.ai.lastRawError = {
          message: data.error || 'El Agente 3 no devolvió JSON válido',
          rawExcerpt: (typeof (data.rawExcerpt || data.raw || raw) === 'string'
            ? data.rawExcerpt || data.raw || raw
            : JSON.stringify(data.rawExcerpt || data.raw || raw || '')
          ).slice(0, 1500),
        }
        if (usage) aiUsageRecord(model, usage, 'build')
        emit()
        return
      }
      if (usage) aiUsageRecord(model, usage, 'build')
      const generatedPackage = normalizeAssistantBuildPayload(data.data)
      if (!generatedPackage.dataJson || typeof generatedPackage.dataJson !== 'object') {
        throw new Error('La IA no devolvio dataJson valido')
      }
      const requisitosCompletos = pedidoOriginal + '\n' + entrada
      const normalizationWarnings = []
      const normalized = normalizeDataJson(generatedPackage.dataJson)
      generatedPackage.dataJson = normalized.data
      if (normalized.changes.length) {
        console.warn('[build normalize] ajustes aplicados:', normalized.changes)
        normalizationWarnings.push(...normalized.changes.map((change) => '[NORMALIZE] ' + change))
      }
      let transactionAdjusted = applyTransactionIntent(generatedPackage.dataJson, requisitosCompletos)
      if (transactionAdjusted.changes.length) {
        console.warn('[build transaction intent] ajustes aplicados:', transactionAdjusted.changes)
        generatedPackage.dataJson = transactionAdjusted.data
        normalizationWarnings.push(...transactionAdjusted.changes.map((change) => '[TRANSACTION] ' + change))
      }
      if (!buildDataJsonEjecutable(generatedPackage.dataJson)) {
        throw new Error('El Agente 3 devolvió un data.json sin formularios ni comandos ejecutables.')
      }

      let lintReport = lintDataJson(generatedPackage.dataJson)
      const initialLintErrorCount = lintErrors(lintReport).length
      if (initialLintErrorCount) {
        console.warn('[build lint] errores deterministas; solicitando una reparación:', lintErrors(lintReport).map((item) => item.code))
        const repairPrompt =
          lintFixPrompt(lintReport, generatedPackage.dataJson) +
          '\n\nCONTRATO DE CONSERVACIÓN: mantené el PEDIDO ORIGINAL y la INTERPRETACIÓN CONFIRMADA. No elimines campos, comandos ni reglas funcionales para silenciar el lint.\n\n' +
          'PEDIDO ORIGINAL:\n' + pedidoOriginal +
          '\n\nINTERPRETACIÓN CONFIRMADA:\n' + entrada
        const repair = await aiCallAgent('agent3_gen', repairPrompt, true, { generationMode: 'repair' })
        if (repair.usage) aiUsageRecord(repair.model, repair.usage, 'build_repair')
        if (repair.ok && repair.data?.data) {
          const repairedPackage = normalizeAssistantBuildPayload(repair.data.data)
          if (repairedPackage.dataJson && typeof repairedPackage.dataJson === 'object') {
            const repairedNormalized = normalizeDataJson(repairedPackage.dataJson)
            let repairedData = repairedNormalized.data
            transactionAdjusted = applyTransactionIntent(repairedData, requisitosCompletos)
            repairedData = transactionAdjusted.data
            const repairedLint = lintDataJson(repairedData)
            const repairedErrorCount = lintErrors(repairedLint).length
            const repairLosses = validateRepairPreservation(generatedPackage.dataJson, repairedData)
            if (repairLosses.length) {
              console.warn('[build lint] reparación descartada por pérdida funcional:', repairLosses)
            }
            if (buildDataJsonEjecutable(repairedData) && !repairLosses.length && repairedErrorCount < initialLintErrorCount) {
              generatedPackage.dataJson = repairedData
              lintReport = repairedLint
              normalizationWarnings.push(...repairedNormalized.changes.map((change) => '[REPAIR NORMALIZE] ' + change))
              normalizationWarnings.push(...transactionAdjusted.changes.map((change) => '[REPAIR TRANSACTION] ' + change))
              console.log('[build lint] reparación aplicada; errores restantes:', repairedErrorCount)
            }
          }
        }
      }
      const finalLintErrors = lintErrors(lintReport)
      if (finalLintErrors.length) {
        throw new Error(
          'El resultado conserva errores estructurales después del intento de reparación: ' +
            finalLintErrors.map((diagnostic) => diagnostic.message).join(' '),
        )
      }
      const reservationDiagnostics = validateReservationRequirements(generatedPackage.dataJson, requisitosCompletos)
      const reservationErrors = reservationDiagnostics.filter((diagnostic) => diagnostic.severity === 'error')
      if (reservationErrors.length) {
        throw new Error(
          'El resultado no cumple los datos de reserva solicitados: ' +
            reservationErrors.map((diagnostic) => diagnostic.message).join(' '),
        )
      }
      state.ai.lastGeneratedPackage = generatedPackage
      state.ai.lastResult = generatedPackage.dataJson
      state.ai.lastWarnings = [
        ...(Array.isArray(data.warnings) ? data.warnings : data.warnings ? [data.warnings] : []).map(diagnosticText),
        ...normalizationWarnings,
        ...lintReport.filter((diagnostic) => diagnostic.severity !== 'info').map(diagnosticText),
        ...reservationDiagnostics.filter((diagnostic) => diagnostic.severity !== 'info').map(diagnosticText),
      ].filter(Boolean)
      edApply(generatedPackage.dataJson, generatedPackage, true)
      state.ai.busy = false
      emit()
    } catch (err) {
      state.ai.busy = false
      if (esProcesoDetenido(err)) {
        toast('Proceso detenido.', 'warning')
        emit()
        return
      }
      state.ai.lastRawError = { message: err.message, rawExcerpt: '' }
      emit()
    }
  }

  function clearAll() {
    state.ai.userPrompt = ''
    state.ai.lastResult = null
    state.ai.lastGeneratedPackage = null
    state.ai.lastWarnings = []
    state.ai.lastRawError = null
    setPrompt('')
    setOriginalRequest('')
    setChat(null)
    setChatInput('')
    emit()
  }

  function onModelChange(value) {
    // Override opcional. Vacio => el backend resuelve el modelo desde ai_settings.
    state.ai.model = value
    try {
      localStorage.removeItem('boston_ai_model_v1')
      if (value) localStorage.setItem('boston_ai_model_override_v2', value)
      else localStorage.removeItem('boston_ai_model_override_v2')
    } catch (e) {
      /* sin persistencia */
    }
    state.config.aiModel = value
    Storage.saveConfig(state.config)
    emit()
  }

  /* ===== edApplyScript — port fiel: aplica el data.json al Custom Script Builder ===== */
  function edApply(result, generatedPackage = null, silent = false, stayInCurrentView = false) {
    const slug = (result.title || 'ia_script').toLowerCase().replace(/[^a-z0-9-_]+/g, '_')
    // FIX: uuid NUEVO por resultado generado (memoizado) — no pisar el script anterior.
    result._uuid = result._uuid || uuidV4()
    const pkg = generatedPackage || state.ai.lastGeneratedPackage || normalizeAssistantBuildPayload(result)
    // FIX preservacion: delegar en edApplyScript (EditorView), que conserva el data.json
    // COMPLETO del agente (skip, visible, coincidence, onLoad, group, tags semanticos...).
    const info = edApplyScript(
      result,
      { config: { uuid: result._uuid, useInternalConfig: true } },
      slug,
      pkg.handlerJs || null,
      pkg.fichaMd || null,
      pkg.logicJson || null,
      { stayInCurrentView },
    )
    if (!silent) toast(
      `Script "${slug}" cargado. UUID: ${info.uuid} - Forms: ${info.forms} - Commands: ${info.commands}. Editá lo que necesites y dale "Guardar + Publicar" en Custom Script.`,
      'success',
      { title: 'Resultado cargado en Custom Script' },
    )
    return slug
  }

  /* ===== Acciones del preview (bindAiPreview, prefijo 'aib') ===== */
  function actionPreviewModal(result, opts = {}) {
    if (opts.realPreview !== true && !mockModeEnabled) return
    const cfg = { config: { uuid: 'preview-' + Date.now(), useInternalConfig: true } }
    showPreviewModal(result, cfg, opts)
  }

  function actionSendReview(result) {
    // El original usa prompt() para pedir el feedback del dev; se mantiene.
    const motivo = window.prompt('Enviar a revisión de DEV\n\n¿Qué está mal o qué falta? (feedback para el dev):', '')
    if (motivo === null) return
    try {
      const q = JSON.parse(localStorage.getItem('boston_dev_queue_v1') || '[]')
      const _dj = JSON.stringify(result, null, 2)
      const _files = { 'data.json': _dj, 'feedback.txt': motivo || '' }
      q.push({
        id: 'rev_' + Date.now().toString(36),
        ts: new Date().toISOString(),
        title: result.title || '(sin titulo)',
        feedback: motivo || '(sin detalle)',
        estado: 'en_revision',
        data: result,
        ir: null, // en el flujo Construir no hay IR (eso es del Importador)
        published: state.cs && state.cs.uuid ? { uuid: state.cs.uuid, name: state.cs.name || '' } : null,
        files: _files,
        original: _dj,
        history: [{ ts: new Date().toISOString(), action: 'enviado a revisión' }],
      })
      localStorage.setItem('boston_dev_queue_v1', JSON.stringify(q.slice(-30)))
      // PERSISTENCIA: el item viaja tambien a la DB (ai_review_queue) — fallback ya quedo en localStorage
      try {
        const _item = q[q.length - 1]
        import('../lib/biaApi').then(({ devQueuePush }) => devQueuePush(_item)).catch(() => {})
      } catch (e) {}
    } catch (e) {
      console.warn('dev queue:', e)
    }
    emit()
    toast(
      '«' + (result.title || 'script') +
        '» está en el módulo Revisión Dev (menú izquierdo): ahí se editan sus archivos, se prueba, se exporta/importa y se marca corregido.',
      'warning',
      { title: 'Enviado a revisión de DEV' },
    )
  }

  function actionClear() {
    state.ai.lastResult = null
    state.ai.lastGeneratedPackage = null
    state.ai.lastWarnings = []
    state.ai.testLog = []
    emit()
  }

  function closeError() {
    state.ai.lastRawError = null
    emit()
  }

  const result = a.lastResult
  const warnings = a.lastWarnings || []
  const formsCount = result ? (result.forms || []).length : 0
  const fieldsCount = result ? (result.forms || []).reduce((s, f) => s + (f.remarks || []).length, 0) : 0

  return (
    <>
      <div className="page-head">
        <div>
          <h1>IA - Construir desde lenguaje natural</h1>
          <p>
            Describí en castellano qué Custom Script necesitás. El agente primero te dice <strong>cómo lo interpretó</strong>;
            corregilo conversando y recién después genera el script.
          </p>
        </div>
        <div>
          {hasKey ? (
            <span className="bia-badge ok">API key en backend</span>
          ) : (
            <span className="bia-badge warn">Sin API key — configurala en Configuración</span>
          )}
        </div>
      </div>

      <div className="card-x">
        <h3>Pedido</h3>
        <CFormTextarea
          className="bia-mono"
          rows={8}
          style={{ fontSize: 13 }}
          placeholder="Ej: Agregar un remark con centro de costo de opciones MKT/IT/VTS y aprobador..."
          value={prompt}
          onChange={(event) => {
            setPrompt(event.target.value)
            state.ai.userPrompt = event.target.value
          }}
        />
        <div style={{ marginTop: 10, maxWidth: 360 }}>
          <CFormLabel>Modelo (aplica a los 3 agentes)</CFormLabel>
          <CFormSelect value={a.model} onChange={(event) => onModelChange(event.target.value)}>
            <option value="">Usar configuración DB</option>
            <option value="claude-opus-5">Claude Opus 5 (override)</option>
            <option value="claude-sonnet-4-5">Claude Sonnet 4.5 (recomendado)</option>
            <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (rápido y barato)</option>
          </CFormSelect>
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <CButton color="success" className="btn-brand" disabled={a.busy || interpBusy} onClick={aiInterpretar}>
            {interpBusy && !chat ? (
              <>
                <CSpinner size="sm" /> Interpretando...
              </>
            ) : a.busy ? (
              <>
                <CSpinner size="sm" /> Generando...
              </>
            ) : (
              <>
                <CIcon icon={icon('cilLightbulb')} /> Interpretar con IA
              </>
            )}
          </CButton>
          {a.busy || interpBusy ? (
            <CButton color="danger" variant="outline" onClick={detenerProceso} title="Detener el proceso de IA">
              ■ Detener
            </CButton>
          ) : null}
          <CButton color="secondary" variant="ghost" onClick={clearAll}>
            <CIcon icon={icon('cilTrash')} /> Limpiar
          </CButton>
        </div>

        {chat ? (
          <div className="ia-chat" style={{ marginTop: 14 }}>
            <div className="ia-chat-head">
              <h4>🧠 Así lo interpretó el agente — corregilo antes de generar</h4>
            </div>
            <div className="ia-chat-messages">
              {chat.messages.map((m, i) => (
                <div key={i} className={`ia-chat-msg ${m.role}`}>
                  <span className="ia-chat-who">{m.role === 'agent' ? 'Agente' : 'Vos'}</span>
                  <p>{m.text}</p>
                </div>
              ))}
              {interpBusy && chat.messages.length ? (
                <div className="ia-chat-msg agent pending">
                  <span className="ia-chat-who">Agente</span>
                  <p>Aplicando tu corrección…</p>
                </div>
              ) : null}
            </div>
            <div className="ia-chat-inputrow">
              <CFormTextarea
                rows={2}
                style={{ fontSize: 13 }}
                placeholder="¿Algo está mal? Decile al agente qué corregir…"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                disabled={a.busy || interpBusy}
              />
              <CButton color="secondary" variant="outline" onClick={aiEnviarCorreccion} disabled={a.busy || interpBusy || chatInput.trim() === ''}>
                Corregir
              </CButton>
            </div>
            <div className="ia-chat-actions">
              <CButton color="success" className="btn-brand" onClick={aiConfirmarYGenerar} disabled={a.busy || interpBusy}>
                {a.busy ? (
                  <>
                    <CSpinner size="sm" /> Generando...
                  </>
                ) : (
                  <>✓ Es correcto — Generar Custom Script</>
                )}
              </CButton>
              {a.busy || interpBusy ? (
                <CButton color="danger" variant="outline" onClick={detenerProceso} title="Detener el proceso de IA">
                  ■ Detener
                </CButton>
              ) : null}
              <CButton color="secondary" variant="ghost" onClick={() => { setChat(null); setChatInput('') }} disabled={a.busy || interpBusy}>
                Descartar
              </CButton>
            </div>
          </div>
        ) : null}
      </div>

      {result ? (
        <div className="card-x" style={{ border: '2px solid #2eb85c' }}>
          <div className="bia-card-head">
            <h3>
              Custom Script generado <span className="bia-badge ok">{result.title || ''}</span>
            </h3>
          </div>

          {warnings.length ? (
            <CAlert color="warning">
              <strong>WARNINGS de la IA:</strong>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </CAlert>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, margin: '8px 0 14px', fontSize: 13 }}>
            <div>
              <strong>Título:</strong> <code>{result.title || ''}</code>
            </div>
            <div>
              <strong>Pasos (forms):</strong> {formsCount}
            </div>
            <div>
              <strong>Campos totales:</strong> {fieldsCount}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, padding: 10, background: '#f8f9fa', borderRadius: 6 }}>
            {mockModeEnabled ? (
              <CButton color="primary" title="Abre el form con llamadas simuladas" onClick={() => actionPreviewModal(result)}>
                <CIcon icon={icon('cilSearch')} /> Vista previa segura
              </CButton>
            ) : null}
            <CButton color="success" variant="outline" title="Usa el token Sabre y puede ejecutar comandos reales" onClick={() => actionPreviewModal(result, { realPreview: true })}>
              <CIcon icon={icon('cilMediaPlay')} /> Vista previa real con token
            </CButton>
            <CButton
              color="warning"
              variant="outline"
              title="No cumple las condiciones: mandalo a la cola del dev con tu feedback"
              onClick={() => actionSendReview(result)}
            >
              <CIcon icon={icon('cilBellExclamation')} /> Enviar a revisión (dev)
            </CButton>
            <CButton color="danger" variant="ghost" onClick={actionClear}>
              <CIcon icon={icon('cilTrash')} /> Descartar
            </CButton>
          </div>

          <FieldsPreview cs={{ forms: result.forms || [], postCommands: result.postCommands || [], title: result.title }} />

          <details style={{ marginTop: 14 }}>
            <summary style={{ cursor: 'pointer', color: '#768192', fontSize: 12 }}>Ver JSON crudo</summary>
            <pre className="bia-pre" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}

      {a.lastRawError ? (
        <div className="card-x" style={{ border: '1px solid #dc2626' }}>
          <div className="bia-card-head">
            <h3 style={{ color: '#dc2626' }}>Error al generar</h3>
            <CButton color="secondary" variant="ghost" size="sm" onClick={closeError}>
              ×
            </CButton>
          </div>
          <CAlert color="danger">
            <strong>{a.lastRawError.message}</strong>
          </CAlert>
          <pre className="bia-pre" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 11.5 }}>
            {a.lastRawError.rawExcerpt || ''}
          </pre>
        </div>
      ) : null}
    </>
  )
}

export default AiBuildView
